import { useState } from "react";
import { Btn, Card, I } from "../../ui";
import { clearQueuedSisterBrowserTasks, requeueFailedSisterBrowserTasks } from "../../api";
import type { SisterQueueFilter } from "../../api/sister-browser";
import { workerStall, workerStallMessage } from "../extension/worker-stall";
import { SisterQueueRows } from "./SisterQueueRows";
import { queueErrorMessage } from "./sister-queue-errors";
import { QUEUE_PAGE_SIZE, useSisterQueue } from "./useSisterQueue";
import { VisureQuota } from "./VisureQuota";

export { EnqueuePreviewModal } from "./EnqueuePreviewModal";

const FILTERS: Array<{ value: SisterQueueFilter; label: string }> = [
  { value: "active", label: "Attive e da controllare" },
  { value: "queued", label: "In attesa" },
  { value: "in_progress", label: "In corso" },
  { value: "needs_review", label: "Da controllare" },
  { value: "failed", label: "Non riuscite" },
  { value: "recovery", label: "Visure da recuperare" },
  { value: "done", label: "Concluse" },
  { value: "skipped", label: "Archiviate / saltate" },
];
const STATE_IT: Record<string, string> = {
  queued: "in attesa", claimed: "presa in carico", running: "in corso", done: "concluse",
  failed: "non riuscite", skipped: "archiviate / saltate", needs_review: "da controllare", paused: "in pausa",
};

export function SisterQueueCard({ refreshKey, onMsg }: { refreshKey: number; onMsg: (msg: string) => void }) {
  const { data, loading, error, view, load, setFilter, setOffset } = useSisterQueue(refreshKey);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);

  async function run(action: () => Promise<string>) {
    setBusy(true);
    try { onMsg(await action()); load(); }
    catch (error) { onMsg(queueErrorMessage(error, "Errore operazione coda")); }
    finally { setBusy(false); }
  }

  async function copyId(id: string) {
    try { await navigator.clipboard.writeText(id); setCopyMsg(`ID richiesta ${id} copiato.`); }
    catch { setCopyMsg(`Copia non disponibile: seleziona e copia l’ID ${id}.`); }
  }

  const total = data?.total ?? 0;
  const queuedCount = data?.byState.find((state) => state.state === "queued")?.count ?? 0;
  const activeCount = (data?.byState ?? [])
    .filter((state) => ["queued", "claimed", "running", "needs_review"].includes(state.state))
    .reduce((count, state) => count + state.count, 0);
  const stall = workerStall(data?.worker, queuedCount);
  const stallMsg = stall ? workerStallMessage(stall) : null;
  const subLine = data ? data.byState.length
    ? data.byState.map((state) => `${STATE_IT[state.state] ?? state.state} ${state.count}`).join(" · ")
    : "nessuna ricerca" : "Caricamento coda…";

  return (
    <div className="sister-queue-card">
    <Card title={data ? activeCount ? `Ricerche sull’estensione · ${activeCount} attive` : "Coda ricerche SISTER" : "Coda ricerche SISTER"}
      sub={subLine} noPad actions={
        <>
          {(data?.recoveryCount ?? 0) > 0 && (
            <Btn size="sm" variant="ghost" onClick={() => { setFilter("recovery"); setExpanded(true); }}>
              Recupera visure ({data?.recoveryCount})
            </Btn>
          )}
          {(data?.retryableCount ?? 0) > 0 && (
            <Btn size="sm" variant="ghost" disabled={busy} title="Rimette in coda le ricerche non riuscite o bloccate che non hanno già inoltrato una visura"
              onClick={() => void run(async () => { const result = await requeueFailedSisterBrowserTasks(); return `${result.requeued} ricerche rimesse in coda.`; })}>
              Riprova le non riuscite ({data?.retryableCount})
            </Btn>
          )}
          {queuedCount > 0 && (
            <Btn size="sm" variant="danger" disabled={busy}
              title="Toglie dalla coda tutte le ricerche in attesa: l’estensione non le eseguirà"
              onClick={() => {
                if (!confirm(`Togliere dalla coda ${queuedCount === 1 ? "la ricerca" : `le ${queuedCount} ricerche`} in attesa? L’estensione non le eseguirà. I necrologi degli ultimi 90 giorni tornano in «Cerca su SISTER», dove puoi scegliere di nuovo chi cercare. Le visure già inoltrate restano in coda.`)) return;
                void run(async () => {
                  const result = await clearQueuedSisterBrowserTasks();
                  return `Coda svuotata: ${result.removed} ricerche in attesa tolte.`
                    + (result.kept ? ` ${result.kept} con visura già inoltrata restano in coda.` : "");
                });
              }}>
              Svuota la coda in attesa ({queuedCount})
            </Btn>
          )}
          <Btn size="sm" variant="ghost" icon={I.refresh} disabled={busy} onClick={load}>Aggiorna</Btn>
          <Btn size="sm" variant="ghost" onClick={() => setExpanded((value) => !value)}>
            {expanded ? "Nascondi" : `Mostra la coda${data ? ` (${total})` : ""}`}
          </Btn>
        </>
      }>
      <div style={{ padding: "12px 16px" }}><VisureQuota refreshKey={refreshKey} /></div>
      {stallMsg && (
        <div className="worker-stall" role="alert">
          <strong>{stallMsg.title}.</strong> {stallMsg.text}
        </div>
      )}
      {queuedCount > 0 && (
        <p className="muted" style={{ margin: 0, padding: "0 16px 12px", fontSize: 12, lineHeight: 1.6 }}>
          L’estensione esegue {queuedCount === 1 ? "la ricerca" : `le ${queuedCount} ricerche`} in attesa nell’ordine dell’elenco: le persone scelte dopo vanno in fondo.
          {" "}⬆ porta una ricerca in cima, ✕ la toglie; «Svuota la coda in attesa» le toglie tutte, così l’estensione cerca solo le persone che sceglierai.
        </p>
      )}
      <div className="row gap-8" style={{ padding: "0 16px 12px", alignItems: "center", flexWrap: "wrap" }}>
        <label className="row gap-8" style={{ alignItems: "center", fontSize: 12 }}>
          Mostra
          <select className="select" value={view.filter} onChange={(event) => {
            const filter = FILTERS.find((filter) => filter.value === event.target.value);
            if (filter) { setFilter(filter.value); setCopyMsg(null); setExpanded(true); }
          }}>
            {FILTERS.map((filter) => <option key={filter.value} value={filter.value}>{filter.label}</option>)}
          </select>
        </label>
        <span className="muted" style={{ fontSize: 12 }} aria-live="polite">
          {data ? `${total} ${total === 1 ? "ricerca" : "ricerche"} · 50 per pagina` : "Caricamento…"}
        </span>
      </div>
      {error && <div role="alert" style={{ padding: "0 16px 12px", color: "var(--danger)", fontSize: 12 }}>{error}</div>}
      {copyMsg && <div role="status" style={{ padding: "0 16px 12px", color: "var(--text-1)", fontSize: 12 }}>{copyMsg}</div>}
      {expanded && (
        <div aria-busy={loading}>
          {!data ? <div className="muted" style={{ padding: 16, fontSize: 13 }}>{error ? "Coda non disponibile. Premi Aggiorna per riprovare." : "Caricamento coda…"}</div>
            : data.items.length ? <SisterQueueRows tasks={data.items} busy={busy} run={run} onCopy={(id) => void copyId(id)} />
            : <div className="muted" style={{ padding: 16, fontSize: 13 }}>Nessuna ricerca per questo filtro.</div>}
          {data && (
            <div className="row gap-8" style={{ padding: "12px 16px", alignItems: "center", flexWrap: "wrap" }}>
              <span className="muted" style={{ fontSize: 12 }}>
                {total ? `${view.offset + 1}–${Math.min(view.offset + data.items.length, total)} di ${total}` : "0 di 0"} · Pagina {Math.floor(view.offset / QUEUE_PAGE_SIZE) + 1} di {Math.max(1, Math.ceil(total / QUEUE_PAGE_SIZE))}
              </span>
              <div className="grow" />
              <Btn size="sm" variant="ghost" disabled={view.offset === 0 || loading} onClick={() => { setOffset(Math.max(0, view.offset - QUEUE_PAGE_SIZE)); setCopyMsg(null); }}>Precedente</Btn>
              <Btn size="sm" variant="ghost" disabled={view.offset + QUEUE_PAGE_SIZE >= total || loading} onClick={() => { setOffset(view.offset + QUEUE_PAGE_SIZE); setCopyMsg(null); }}>Successiva</Btn>
            </div>
          )}
        </div>
      )}
    </Card>
    </div>
  );
}
